import React from 'react';
import { Icon } from './Icon';

export interface FeatureGateProps {
  /** When false, children are shown disabled/greyed with a lock + tooltip. */
  enabled: boolean;
  /** Tooltip text when locked, e.g. "Disponible a partir del plan Max". The
   *  caller owns the copy/i18n (shared component, label via prop). */
  message?: string;
  /** Extra classes for the wrapper (e.g. width/display for inline controls). */
  className?: string;
  /** Hide the centered lock glyph (keep only the greyed-out + tooltip). */
  hideLockIcon?: boolean;
  /** Visible text next to the lock (e.g. "Not included in the selected plan"),
   *  in the warning tone, centred over the locked area. For large locked areas,
   *  where a lone glyph and a hover tooltip don't say why nothing responds. When
   *  a screen locks several sections, give the label to the first gate only. */
  lockLabel?: string;
  children: React.ReactNode;
}

/**
 * Wraps any UI that is gated behind a plan entitlement. When `enabled` it is a
 * transparent passthrough; when locked it greys the children out, blocks
 * interaction, and surfaces `message` as a tooltip (with a centered lock glyph).
 *
 * Pure presentation — the *decision* (which feature, which plan) is the
 * caller's via `enabled` + `message` (see tenant `useEntitlements`). Admin does
 * not use this (no gating there).
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
  enabled,
  message,
  className = '',
  hideLockIcon = false,
  lockLabel,
  children,
}) => {
  if (enabled) return <>{children}</>;

  return (
    <div
      className={`relative ${className}`}
      title={message}
      aria-disabled="true"
      data-feature-locked="true"
    >
      {/* `inert`: pointer-events-none only stops the mouse — the locked
          controls were still reachable with Tab and a screen reader. */}
      <div className="opacity-40 grayscale pointer-events-none select-none" inert>{children}</div>
      <div className="absolute inset-0 z-10 flex items-center justify-center cursor-not-allowed">
        {lockLabel ? (
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 shadow-sm text-xs font-bold bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
            <Icon name="lock" className="!text-[16px]" aria-hidden="true" />
            {lockLabel}
          </span>
        ) : !hideLockIcon && (
          <span className="inline-flex rounded-full p-1 shadow-sm bg-white/80 dark:bg-background-dark/80" aria-hidden="true">
              <Icon name="lock" className="!text-[18px] text-text-sub dark:text-slate-300" />
            </span>
        )}
        {message ? <span className="sr-only">{message}</span> : null}
      </div>
    </div>
  );
};

export default FeatureGate;
